import { useState, useEffect, useRef, useCallback } from "react";
import { onChange } from "@/lib/changes";
import { visibleInterval } from "@/lib/poll";
import { useNavigate } from "@/lib/useNav";
import client from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { playNotify } from "@/lib/chatSound";
import { showDesktop } from "@/lib/desktopNotify";
import { pushOnHere } from "@/lib/webpush";
import {
  Bell, UserPlus, CheckCircle2, AtSign, CalendarClock, AlertTriangle, X, Trash2, MessageCircle, ListChecks,
  Lock, Pin,
} from "lucide-react";

const ICONS = {
  task_assigned: { icon: UserPlus, cls: "text-sky-600 bg-sky-50" },
  account_locked: { icon: Lock, cls: "text-red-600 bg-red-50" },
  task_status: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
  task_subtask: { icon: ListChecks, cls: "text-sky-600 bg-sky-50" },
  mention: { icon: AtSign, cls: "text-violet-600 bg-violet-50" },
  chat_mention: { icon: MessageCircle, cls: "text-violet-600 bg-violet-50" },
  task_linked: { icon: UserPlus, cls: "text-sky-600 bg-sky-50" },
  due_today: { icon: CalendarClock, cls: "text-amber-600 bg-amber-50" },
  overdue: { icon: AlertTriangle, cls: "text-red-600 bg-red-50" },
  notice: { icon: Pin, cls: "text-amber-600 bg-amber-50" },
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();
  const navigate = useNavigate();
  const { user } = useAuth();
  const prevUnread = useRef(undefined);

  const load = useCallback(() => {
    client.get("/notifications").then(({ data }) => {
      const u = data.unread || 0;
      window.dispatchEvent(new CustomEvent("notif-unread", { detail: u }));
      setItems(data.items || []);
      setUnread(u);
      if (prevUnread.current !== undefined && u > prevUnread.current) {
        const top = (data.items || []).find((x) => !x.read);
        if (top?.type === "notice") { prevUnread.current = u; return; }
        if (user?.preferences?.notification_sound !== false) playNotify();
        if (user?.preferences?.notification_desktop === true) {
          if (top && !(pushOnHere() && ["mention", "chat_mention"].includes(top.type))) showDesktop({
            title: top.title || "New notification",
            body: top.message || "",
            tag: `notif-${top.id}`,
            url: top.link || "/notifications",
            onClick: () => navigate(top.link || "/notifications"),
          });
        }
      }
      prevUnread.current = u;
    }).catch(() => {});
  }, [user, navigate]);

  useEffect(() => { load(); const stop = visibleInterval(load, 60000); const off = onChange("notif", load); return () => { stop(); off(); }; }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openPanel = () => { setOpen(true); load(); };

  const markRead = (n) => {
    if (n.read) return;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    client.post(`/notifications/${n.id}/read`).catch(() => {});
  };

  const onItem = (n, e) => {
    const newTab = e && (e.ctrlKey || e.metaKey || e.button === 1);
    markRead(n);
    if (newTab) { if (n.link) window.open(n.link, "_blank", "noopener"); return; }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const removeOne = (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read) setUnread((u) => Math.max(0, u - 1));
    client.post(`/notifications/${n.id}/dismiss`).catch(() => {});
  };

  const clearAll = () => {
    setItems([]);
    setUnread(0);
    client.post("/notifications/dismiss-all").catch(() => {});
  };

  const viewAll = () => { setOpen(false); navigate("/notifications"); };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        className="relative rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="notifications-panel">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-800">Notifications</p>
            {items.length > 0 && (
              <button onClick={clearAll} data-testid="notifications-clear-all" className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-600">
                <Trash2 className="h-3.5 w-3.5" /> Clear all
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-slate-400">You're all caught up</p>
            ) : (
              items.map((n) => {
                const meta = ICONS[n.type] || { icon: Bell, cls: "text-slate-500 bg-slate-100" };
                const Icon = meta.icon;
                return (
                  <div
                    key={n.id}
                    onClick={(e) => onItem(n, e)}
                    onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); onItem(n, e); } }}
                    data-testid={`notification-item-${n.id}`}
                    className={cn(
                      "group flex w-full cursor-pointer items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors",
                      n.read ? "bg-slate-100/70 hover:bg-slate-100" : "bg-white hover:bg-sky-50/60"
                    )}
                  >
                    <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full", n.read ? "bg-slate-200 text-slate-400" : meta.cls)}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cn("text-sm", n.read ? "font-normal text-slate-500" : "font-medium text-slate-800")}>{n.title}</p>
                      {n.message && <p className={cn("truncate text-xs", n.read ? "text-slate-400" : "text-slate-500")}>{n.message}</p>}
                      <p className="mt-0.5 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-sky-500" />}
                      <button
                        onClick={(e) => removeOne(e, n)}
                        data-testid={`notification-clear-${n.id}`}
                        title="Clear"
                        className="rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <button onClick={viewAll} data-testid="notifications-view-all" className="block w-full border-t border-slate-100 px-4 py-2.5 text-center text-xs font-semibold text-sky-600 hover:bg-sky-50">
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
